import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "./components/Header";
import LoadingLogo from "./components/LoadingLogo";
import { getAuthenticatedAxios } from "./utils/api";
import "./index.css";

const clone = (value) => JSON.parse(JSON.stringify(value));

const getValueByPath = (source, path) => {
  if (!path) return source;
  return path.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined || typeof acc !== "object") {
      return undefined;
    }
    return acc[key];
  }, source);
};

const setValueByPath = (target, path, value) => {
  const parts = path.split(".");
  let cursor = target;

  for (let index = 0; index < parts.length - 1; index += 1) {
    const key = parts[index];
    const nextValue = cursor[key];
    if (!nextValue || typeof nextValue !== "object") {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }

  cursor[parts[parts.length - 1]] = value;
};

const serializeSection = (value) => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return JSON.stringify(value ?? {}, null, 2);
};

const parseSection = (raw, sample) => {
  if (typeof sample === "number") {
    const normalized = String(raw ?? "").trim();
    if (!normalized) {
      throw new Error("Number field cannot be empty");
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
      throw new Error("Invalid number value");
    }
    if (Number.isInteger(sample) && !Number.isInteger(parsed)) {
      throw new Error("Value must be an integer");
    }
    return parsed;
  }
  if (typeof sample === "string") {
    return raw;
  }
  return JSON.parse(raw);
};

function PromptManagement() {
  const [templates, setTemplates] = useState({});
  const [metadata, setMetadata] = useState({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSaving, setTemplatesSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [drafts, setDrafts] = useState({});
  const navigate = useNavigate();
  const { t } = useTranslation();

  const translateLabel = useCallback(
    (key, fallback) => {
      const translated = t(key);
      return translated && translated !== key ? translated : fallback;
    },
    [t],
  );

  const getTemplateLabel = useCallback(
    (templateName) =>
      translateLabel(`promptManagement.templates.${templateName}`, templateName),
    [translateLabel],
  );

  const getSectionLabel = useCallback(
    (sectionPath) =>
      translateLabel(`promptManagement.sections.${sectionPath}`, sectionPath),
    [translateLabel],
  );

  const initializeDrafts = useCallback((templateName, nextTemplates, nextMetadata) => {
    const payload = nextTemplates[templateName] || {};
    const sections = nextMetadata[templateName]?.editable_sections || [];
    const nextDrafts = {};
    sections.forEach((sectionPath) => {
      nextDrafts[sectionPath] = serializeSection(getValueByPath(payload, sectionPath));
    });
    setDrafts(nextDrafts);
  }, []);

  const loadTemplates = useCallback(
    async (templateNameOverride = "") => {
      setTemplatesLoading(true);
      try {
        const authAxios = getAuthenticatedAxios();
        const response = await authAxios.get("/api/v2/prompts/templates");
        const responseTemplates = response.data?.templates || {};
        const responseMetadata = response.data?.metadata || {};
        const templateNames = Object.keys(responseTemplates);

        const nextTemplateName =
          templateNameOverride ||
          selectedTemplate ||
          templateNames[0] ||
          "";

        setTemplates(responseTemplates);
        setMetadata(responseMetadata);
        setSelectedTemplate(nextTemplateName);
        if (nextTemplateName) {
          initializeDrafts(nextTemplateName, responseTemplates, responseMetadata);
        }
      } catch (error) {
        alert(`${t("common.error")}: ${error.message}`);
      } finally {
        setTemplatesLoading(false);
      }
    },
    [initializeDrafts, selectedTemplate, t],
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleTemplateChange = (templateName) => {
    setSelectedTemplate(templateName);
    initializeDrafts(templateName, templates, metadata);
  };

  const handleDraftChange = (sectionPath, value) => {
    setDrafts((prev) => ({
      ...prev,
      [sectionPath]: value,
    }));
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;

    const currentTemplate = templates[selectedTemplate] || {};
    const editableSections = metadata[selectedTemplate]?.editable_sections || [];
    const nextTemplatePayload = clone(currentTemplate);

    try {
      editableSections.forEach((sectionPath) => {
        const sampleValue = getValueByPath(currentTemplate, sectionPath);
        const rawDraft = drafts[sectionPath] ?? serializeSection(sampleValue);
        const parsed = parseSection(rawDraft, sampleValue);
        setValueByPath(nextTemplatePayload, sectionPath, parsed);
      });
    } catch (error) {
      alert(`${t("promptManagement.invalidSectionValue")}: ${error.message}`);
      return;
    }

    setTemplatesSaving(true);
    try {
      const authAxios = getAuthenticatedAxios();
      await authAxios.post("/api/v2/prompts/templates", {
        templates: {
          [selectedTemplate]: nextTemplatePayload,
        },
      });
      await loadTemplates(selectedTemplate);
      alert(t("promptManagement.promptsSaved"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      alert(
        `${t("promptManagement.promptSaveError")}: ${
          typeof detail === "string" ? detail : error.message
        }`,
      );
    } finally {
      setTemplatesSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!selectedTemplate) return;

    const resetPrompt = t("promptManagement.confirmReset");
    const resetMessage =
      resetPrompt && resetPrompt !== "promptManagement.confirmReset"
        ? resetPrompt
        : "Reset templates to defaults?";
    if (!window.confirm(resetMessage)) {
      return;
    }

    setTemplatesSaving(true);
    try {
      const authAxios = getAuthenticatedAxios();
      await authAxios.post("/api/v2/prompts/templates/reset", {
        template_names: [selectedTemplate],
      });
      await loadTemplates(selectedTemplate);
      alert(t("promptManagement.promptsReset"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      alert(
        `${t("promptManagement.promptSaveError")}: ${
          typeof detail === "string" ? detail : error.message
        }`,
      );
    } finally {
      setTemplatesSaving(false);
    }
  };

  const templateNames = Object.keys(templates);
  const editableSections = metadata[selectedTemplate]?.editable_sections || [];
  const dynamicFields = metadata[selectedTemplate]?.dynamic_fields || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {t("promptManagement.title")}
            </h2>
            <button
              onClick={() => navigate("/")}
              className="px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t("searchHistory.backToSearch")}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("promptManagement.template")}
          </label>
          <select
            value={selectedTemplate}
            onChange={(event) => handleTemplateChange(event.target.value)}
            className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={templatesLoading || templatesSaving}
          >
            {templateNames.map((name) => (
              <option key={name} value={name}>
                {getTemplateLabel(name)}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6">
            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingLogo
                  size="md"
                  message={t("promptManagement.loadingPrompts")}
                />
              </div>
            ) : (
              <div className="space-y-6">
                {editableSections.map((sectionPath) => (
                  <div key={sectionPath}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getSectionLabel(sectionPath)}
                    </label>
                    {dynamicFields[sectionPath]?.length > 0 && (
                      <p className="text-xs text-gray-500 mb-2">
                        {t("promptManagement.dynamicFieldsLabel")}: {dynamicFields[sectionPath].join(", ")}
                      </p>
                    )}
                    {typeof getValueByPath(templates[selectedTemplate] || {}, sectionPath) === "number" ? (
                      <input
                        type="number"
                        step="1"
                        value={drafts[sectionPath] || ""}
                        onChange={(event) =>
                          handleDraftChange(sectionPath, event.target.value)
                        }
                        className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        disabled={templatesSaving}
                      />
                    ) : (
                      <textarea
                        value={drafts[sectionPath] || ""}
                        onChange={(event) =>
                          handleDraftChange(sectionPath, event.target.value)
                        }
                        className="w-full min-h-[140px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                        disabled={templatesSaving}
                      />
                    )}
                  </div>
                ))}

                {editableSections.length === 0 && (
                  <div className="text-sm text-gray-500">
                    {t("promptManagement.noEditableSections")}
                  </div>
                )}

                <div className="flex justify-between pt-6 border-t">
                  <div className="space-x-3">
                    <button
                      onClick={resetTemplate}
                      disabled={templatesSaving || !selectedTemplate}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {t("promptManagement.resetCurrentTab")}
                    </button>
                  </div>

                  <button
                    onClick={saveTemplate}
                    disabled={templatesSaving || !selectedTemplate}
                    className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {templatesSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("promptManagement.savingPrompts")}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t("promptManagement.savePrompts")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default PromptManagement;
